import { Redirect, router } from "expo-router";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Platform } from "react-native";
import SocketIOClient from "socket.io-client";
import * as yup from "yup";
import { z } from "zod";
import { IconButton } from "~/components/IconButton";
import { Screen } from "~/components/Screen";
import { Spacer } from "~/components/Spacer";
import { Spinner } from "~/components/Spinner";
import { Text } from "~/components/Text";
import { View } from "~/components/View";
import { environment } from "~/environment";
import { useAlert } from "~/hooks/useAlert";
import { state } from "~/store";
import { styleConstants } from "~/style/styleConstants";
import { assert } from "~/utils/assert";
import { assertSchema } from "~/utils/assertSchema";
import { FormattedMessage, getLocaleURL } from "../intl";
import { LiveVideoInProgress } from "./LiveVideoInProgress";
import { userDetailsSchema } from "./userDetailsSchema";
import {
  RTCPeerConnection,
  RTCSessionDescription,
  mediaDevices,
} from "./web-rtc";
import type { MediaStream, RTCIceCandidate } from "./web-rtc";
import type { Socket } from "socket.io-client";

export function LiveVideoChatScreen() {
  const propsToValidate = {
    name: state.localUserDetails.name,
    age: state.localUserDetails.age,
    sex: state.localUserDetails.sex,
    uniqueUserId: state.uniqueUserId,
    preferredCameraFacingMode: state.preferredCameraFacingMode,
    preferredIsLocalMicOn: state.preferredIsLocalMicOn,
    preferredAudioOutput: state.preferredAudioOutput,

    // For web
    videoInputDeviceId: state.videoInputDeviceId,
    audioInputDeviceId: state.audioInputDeviceId,
  };

  const schema = userDetailsSchema.shape({
    preferredCameraFacingMode: yup
      .string()
      .oneOf(["user", "environment"])
      .default("user"),
    preferredIsLocalMicOn: yup.boolean().default(true),
    preferredAudioOutput: yup
      .string()
      .oneOf(["SPEAKER", "BLUETOOTH", "WIRED_HEADSET"])
      .default("SPEAKER"),
    uniqueUserId: yup
      .string()
      .required()
      .test({
        name: "uniqueUserId",
        message: "not a valid uniqueUserId",
        test: (value) => {
          return value.length >= 8 || yup.string().email().isValidSync(value);
        },
      }),

    // For web
    videoInputDeviceId: yup.string().min(1).required(),
    audioInputDeviceId: yup.string(),
  });

  try {
    const validatedProps = schema.validateSync(propsToValidate);

    return <LiveVideoChatScreenValidated validatedProps={validatedProps} />;
  } catch (_error) {
    return <Redirect href={getLocaleURL("/")} />;
  }
}

const LiveVideoChatScreenValidated = observer(function LiveVideoChatScreen({
  validatedProps,
}: {
  validatedProps: {
    preferredCameraFacingMode: "user" | "environment";
    preferredIsLocalMicOn: boolean;
    videoInputDeviceId: string;
    audioInputDeviceId?: string | undefined;
    uniqueUserId: string;
    name: string;
    age: number;
    sex: "male" | "female" | "other";
  };
}) {
  const alert = useAlert();

  const facingMode = validatedProps.preferredCameraFacingMode;
  const isLocalMicOn = validatedProps.preferredIsLocalMicOn;

  const localUserDetails = useMemo(() => {
    return {
      name: validatedProps.name,
      age: validatedProps.age,
      sex: validatedProps.sex,
      userId: validatedProps.uniqueUserId,
    };
  }, [
    validatedProps.name,
    validatedProps.age,
    validatedProps.sex,
    validatedProps.uniqueUserId,
  ]);

  const [userMediaWithDimensions, setUserMediaWithDimensions] =
    useState<null | {
      userMedia: MediaStream;
      dimensions: { width: number; height: number };
    }>(null);

  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(
    function initializeUserMediaEffect() {
      // Here we keep a list od cleanup functions we use to clean up the effect
      // In the effect cleanup we go through all of them and call them
      const cleanupList: (() => void)[] = [];

      async function runInitializeUserMediaEffect() {
        const webConstraints: { video?: any; audio?: any } = {};

        function getConstraints() {
          // On web, we use the deviceId to set the camera and microphone
          // and on mobile we use the facingMode to set the camera.
          if (Platform.OS === "web") {
            if (validatedProps.audioInputDeviceId) {
              webConstraints.audio = {
                deviceId: validatedProps.audioInputDeviceId,
              };
            }
            webConstraints.video = {
              deviceId: validatedProps.videoInputDeviceId,
            };

            return webConstraints;
          } else {
            return { audio: true, video: { facingMode: facingMode } };
          }
        }

        const constraints = getConstraints();

        // Calling this requests camera permission, but the permissions
        // should already be granted on the settings screen
        console.log("[🔌] Requesting user media with constraints", constraints);

        const userMedia = await mediaDevices.getUserMedia(constraints);

        console.log("[🔌] Got user media", userMedia);

        const trackList = userMedia.getTracks();

        cleanupList.push(() => {
          for (const track of trackList) {
            track.stop();
            userMedia.removeTrack?.(track); // Added for web
          }
          userMedia.release?.(true);
        });

        const videoTrack = userMedia.getVideoTracks()[0];

        if (!videoTrack) {
          console.error("[🔌] No video track found in user media");
          alert("No video input devices found");
          router.replace(getLocaleURL("/"));
          return;
        }

        // @ts-expect-error This is an ugly hack, but we use it to keep track of facingMode
        videoTrack.__facingMode = facingMode;

        if (isLocalMicOn === false) {
          for (const track of trackList) {
            if (track.kind === "audio") {
              track.enabled = false;
            }
          }
        }

        // Expected result: {"frameRate": 30, "height": 720, "width": 1280}
        const videoSettings = videoTrack?.getSettings();

        assertSchema(
          videoSettings,
          z.object({
            width: z.number(),
            height: z.number(),
            frameRate: z.number(),
          }),
        );

        const localStreamDimensions =
          Platform.OS === "web"
            ? {
                width: videoSettings.width,
                height: videoSettings.height,
              }
            : {
                // For some reason sometimes width is height and height is width
                // so we take the min of the two for the width and
                // the max of the two for the height
                width: Math.min(videoSettings.width, videoSettings.height),
                height: Math.max(videoSettings.width, videoSettings.height),
              };

        setUserMediaWithDimensions({
          userMedia,
          dimensions: localStreamDimensions,
        });
      }

      runInitializeUserMediaEffect();

      return () => {
        for (const cleanup of cleanupList) {
          cleanup();
        }
      };
    },
    [
      alert,
      facingMode,
      isLocalMicOn,
      validatedProps.audioInputDeviceId,
      validatedProps.videoInputDeviceId,
    ],
  );

  useEffect(
    function initializeSocketEffect() {
      console.log("[🔌] Creating socket");

      const socket = SocketIOClient("https://fast.bokbok.io", {
        transports: ["websocket"],
        query: {
          mode: "guest",
          name: localUserDetails.name,
          age: localUserDetails.age,
          sex: localUserDetails.sex,
          userId: localUserDetails.userId,
        },
        autoConnect: false,
      });
      console.log("[🔌] Socket created");

      socket.connect();

      setSocket(socket);

      return () => {
        console.log("[🔌] Disconnecting socket");
        socket.disconnect();
        setSocket(null);
      };
    },
    [localUserDetails],
  );

  if (!userMediaWithDimensions || !socket) {
    return (
      <Screen withBottomInset={false} withTopInset preventScroll>
        <View flex>
          <View style={{ height: 350 }} centerContent>
            <Text sizeLarge weightSemiBold>
              <FormattedMessage
                id="HWSTd3"
                defaultMessage="Setting up your camera..."
                description="Setting up your camera text on LiveVideoChatScreen"
              />
            </Text>

            <Spacer extraLarge />
            <Spacer extraLarge />

            <Spinner size="large" />
          </View>

          <IconButton
            absoluteTopLeftLarge
            iconName="arrow-back"
            iconColor={styleConstants.colorTextDark}
            iconSize={24}
            onPress={() => {
              if (router.canGoBack()) {
                router.back();
              } else {
                router.replace(getLocaleURL("/"));
              }
            }}
          />
        </View>
      </Screen>
    );
  }

  const { userMedia, dimensions } = userMediaWithDimensions;

  return (
    <LiveVideoChatScreenWithTrack
      userMedia={userMedia}
      dimensions={dimensions}
      socket={socket}
      localUserDetails={localUserDetails}
    />
  );
});

const LiveVideoChatScreenWithTrack = observer(
  function LiveVideoChatScreenWithTrack({
    userMedia,
    dimensions,
    socket,
    localUserDetails,
  }: {
    userMedia: MediaStream;
    dimensions: { width: number; height: number };
    socket: Socket;
    localUserDetails: {
      name: string;
      age: number;
      sex: "male" | "female" | "other";
    };
  }) {
    const [connectionId, setConnectionId] = useState(
      Math.random().toString(16).slice(-8),
    );
    const resetConnectionId = useCallback(() => {
      setConnectionId(Math.random().toString(16).slice(-8));
    }, []);

    const [userDetails, setUserDetails] = useState<{
      name: string;
      age: number;
      sex: "male" | "female" | "other";
    } | null>(null);
    const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

    useEffect(
      function initializeEffect() {
        // Here we keep a list od cleanup functions we use to clean up the effect
        // In the effect cleanup we go through all of them and call them
        const cleanupList: (() => void)[] = [];

        async function runInitializeEffect() {
          setRemoteStream(null);
          setUserDetails(null);

          const peerConnection = new RTCPeerConnection({
            iceServers: [
              { urls: "stun:stun.l.google.com:19302" },
              { urls: "stun:stun1.l.google.com:19302" },
              { urls: "stun:stun2.l.google.com:19302" },
              {
                urls: "turn:turn.bokbok.io:3478",
                username: environment.STUN_SERVER_USERNAME,
                credential: environment.STUN_SERVER_PASSWORD,
              },
            ],
          });

          peerConnection.addEventListener("connectionstatechange", (_event) => {
            console.log(
              `[💛] Connection state change: ${peerConnection.connectionState}`,
            );
          });

          cleanupList.push(() => {
            // TODO: check if this is the right way to close the peer connection
            // maybe we should check if it's already closed or if it's currently open
            peerConnection.close();
          });

          const trackList = userMedia.getTracks();
          for (const track of trackList) {
            peerConnection.addTrack(track, userMedia);
          }

          // Setup remote stream handling

          function onTrack(event: { streams: MediaStream[] }) {
            console.log(
              `[💛] Got "track" event with ${event.streams.length} streams!`,
            );

            for (const stream of event.streams) {
              setRemoteStream(stream);
            }
          }

          peerConnection.addEventListener("track", onTrack as any);

          cleanupList.push(() => {
            peerConnection.removeEventListener("track", onTrack as any);
          });

          // Create offer, and gather ICE candidates
          const offerAndIceCandidatesOrError = await new Promise<
            | {
                iceCandidateList: RTCIceCandidate[];
                offer: {
                  sdp: string;
                  type: "offer";
                };
                error: null;
              }
            | { error: Error }
          >(
            // eslint-disable-next-line no-async-promise-executor
            async function runPromise(resolve) {
              try {
                const iceCandidateList: RTCIceCandidate[] = [];

                const onICECandidate = (event: {
                  candidate: RTCIceCandidate | null;
                }) => {
                  if (event.candidate) {
                    iceCandidateList.push(event.candidate);
                  } else {
                    console.log(
                      `[🧊] Finished gathering ICE candidates. Got ${iceCandidateList.length} candidates.`,
                    );

                    peerConnection.removeEventListener(
                      "icecandidate",
                      onICECandidate,
                    );
                    clearTimeout(timeoutId);
                    resolve({ iceCandidateList, offer, error: null });
                  }
                };

                console.log("[🧊] Starting gathering ICE candidates...");
                peerConnection.addEventListener("icecandidate", onICECandidate);
                const offer = await peerConnection.createOffer({
                  iceRestart: true,
                });

                assertSchema(
                  offer,
                  z.object({ sdp: z.string(), type: z.literal("offer") }),
                );

                console.log("[💜] setLocalDescription(offer);");

                await peerConnection.setLocalDescription(offer);

                const timeoutId = setTimeout(() => {
                  assert(iceCandidateList != null, "iceCandidateList is null");
                  assert(
                    iceCandidateList.length !== 0,
                    "iceCandidateList is empty",
                  );

                  console.log(
                    `[🧊] Finished gathering ICE candidates in timeout. Got ${iceCandidateList.length} candidates.`,
                  );

                  resolve({ iceCandidateList, offer, error: null });
                  peerConnection.removeEventListener(
                    "icecandidate",
                    onICECandidate,
                  );
                }, 3000);
              } catch (error) {
                resolve({
                  error:
                    error instanceof Error ? error : new Error(String(error)),
                });
              }
            },
          );

          if (offerAndIceCandidatesOrError.error) {
            console.error(
              "[🔌] Error creating offer and gathering ICE candidates",
              offerAndIceCandidatesOrError.error,
            );
            if (router.canGoBack()) {
              router.back();
            } else {
              router.replace(getLocaleURL("/video-settings"));
            }

            return;
          }

          const { iceCandidateList, offer } = offerAndIceCandidatesOrError;

          // Here we attach a listener for ICE candidate events, for debugging purposes
          const onICECandidateEvent = () =>
            console.log("[🧊] Received icecandidate event | debugging only");
          peerConnection.addEventListener("icecandidate", onICECandidateEvent);
          cleanupList.push(() => {
            console.log("[🧊] Removing icecandidate event listener");

            peerConnection.removeEventListener(
              "icecandidate",
              onICECandidateEvent,
            );
          });

          console.log("[🔌] Emitting 'New user connected'");
          socket.emit("New user connected", {
            iceCandidateList: iceCandidateList,
            offer: offer,
          });

          socket.on("Answer to userA", async (data: any) => {
            console.log(`[🔌] Got event 'Answer to userA'`);

            // We are the userA in this case
            assertSchema(
              data,
              z.object({
                answer: z.object({
                  sdp: z.string(),
                  type: z.literal("answer"),
                }),
                iceCandidateList: z.array(
                  z.object({
                    candidate: z.string(),
                    sdpMLineIndex: z.number(),
                    sdpMid: z.string(),
                  }),
                ),
                userDetails: z.object({
                  name: z.string(),
                  age: z.number(),
                  sex: z.enum(["male", "female", "other"]),
                }),
              }),
            );

            console.log("[💛] Creating and setting session description");
            const sessionDescription = new RTCSessionDescription(data.answer);
            await peerConnection.setRemoteDescription(sessionDescription);

            console.log("[🧊] Adding ice candidates to peer connection");

            setUserDetails(data.userDetails);

            for (const iceCandidate of data.iceCandidateList) {
              await peerConnection.addIceCandidate({
                candidate: iceCandidate.candidate,
                sdpMLineIndex: iceCandidate.sdpMLineIndex,
                sdpMid: iceCandidate.sdpMid,
              });
            }
          });

          cleanupList.push(() => {
            console.log("[🔌] Removing 'Answer to userA' event listener");
            // This removes all event listeners
            socket.off("Answer to userA");
          });

          socket.on("Reset connection", () => {
            console.log(`[🔌] Got event 'Reset connection'`);
            resetConnectionId();
          });

          cleanupList.push(() => {
            console.log("[🔌] Removing 'Reset connection' event listener");
            // This removes all event listeners
            socket.off("Reset connection");
          });

          socket.on("Match with userA", async (data: any) => {
            console.log(`[🔌] Got event 'Match with userA'`);

            if (peerConnection.connectionState === "connected") {
              console.log("[🔌] Already connected, disconnecting");
              peerConnection.close();
              return;
            }

            // We are the userB in this case
            try {
              assertSchema(
                data,
                z.object({
                  iceCandidateList: z.array(
                    z.object({
                      candidate: z.string(),
                      sdpMLineIndex: z.number(),
                      sdpMid: z.string(),
                    }),
                  ),
                  offer: z.object({
                    sdp: z.string(),
                    type: z.literal("offer"),
                  }),
                  userDetails: z.object({
                    name: z.string(),
                    age: z.number(),
                    sex: z.enum(["male", "female", "other"]),
                  }),
                }),
              );

              setUserDetails(data.userDetails);

              console.log("[💛] Creating and setting remote description");
              const remoteDescription = new RTCSessionDescription(data.offer);
              await peerConnection.setRemoteDescription(remoteDescription);

              console.log("[🧊] Adding ice canidates to peer connection");

              for (const iceCandidate of data.iceCandidateList) {
                await peerConnection.addIceCandidate({
                  candidate: iceCandidate.candidate,
                  sdpMLineIndex: iceCandidate.sdpMLineIndex,
                  sdpMid: iceCandidate.sdpMid,
                });
              }

              console.log(
                "[💛] Creating answer and setting it as local description",
              );

              const answer = await peerConnection.createAnswer();
              assertSchema(
                answer,
                z.object({ sdp: z.string(), type: z.literal("answer") }),
              );

              // We used the offer from other party to create an aswer
              // now we set the answer as local description and gather
              // ICE candidates
              const iceCandidateList = await new Promise<RTCIceCandidate[]>(
                // eslint-disable-next-line no-async-promise-executor
                async function runPromise(resolve, reject) {
                  try {
                    const iceCandidateList: RTCIceCandidate[] = [];

                    const onICECandidate = (event: {
                      candidate: RTCIceCandidate | null;
                    }) => {
                      if (event.candidate) {
                        console.log(
                          "[🧊] answer | onICECandidate event with candidate",
                        );
                        iceCandidateList.push(event.candidate);
                      } else {
                        console.log(
                          `[🧊] answer | Finished gathering ICE candidates. Got ${iceCandidateList.length} candidates. Clearing timeout ${timeoutId}`,
                        );

                        peerConnection.removeEventListener(
                          "icecandidate",
                          onICECandidate,
                        );
                        resolve(iceCandidateList);
                        clearTimeout(timeoutId);
                      }
                    };

                    console.log(
                      "[🧊] answer | Starting gathering ICE candidates...",
                    );
                    peerConnection.addEventListener(
                      "icecandidate",
                      onICECandidate,
                    );

                    console.log("[💜] setLocalDescription(answer);");

                    await peerConnection.setLocalDescription(answer);

                    const timeoutId = setTimeout(() => {
                      assert(
                        iceCandidateList != null,
                        "iceCandidateList is null",
                      );
                      assert(
                        iceCandidateList.length > 0,
                        "No iceCandidateList",
                      );

                      console.log(
                        `[🧊] answer | Finished gathering ICE candidates in timeout. Got ${iceCandidateList.length} candidates.`,
                      );

                      resolve(iceCandidateList);
                      peerConnection.removeEventListener(
                        "icecandidate",
                        onICECandidate,
                      );
                    }, 3000);
                  } catch (error) {
                    reject(error);
                  }
                },
              );

              console.log("[🔌] Emitting 'Answer to userA'");
              socket.emit("Answer to userA", {
                answer: answer,
                iceCandidateList: iceCandidateList,
              });
            } catch (error) {
              console.log("[🔌] Error in 'Match with userA'", error);
            }
          });

          cleanupList.push(() => {
            console.log("[🔌] Removing 'Match with userA' event listener");
            // This removes all event listeners
            socket.off("Match with userA");
          });
        }

        runInitializeEffect();

        return () => {
          for (const cleanup of cleanupList) {
            cleanup();
          }
        };
      },
      [userMedia, connectionId, resetConnectionId, socket],
    );

    return (
      <Screen
        withBottomInset={false}
        withTopInset={false}
        scrollEnabled={false}
        keyboardAvoidingViewProps={{ enabled: false }}
      >
        <LiveVideoInProgress
          localStream={userMedia}
          localStreamDimensions={dimensions}
          resetConnectionId={resetConnectionId}
          socket={socket}
          localUserDetails={localUserDetails}
          remoteStream={remoteStream}
          remoteUserDetails={userDetails}
        />
      </Screen>
    );
  },
);
